// core/services/bookmarksService.ts — B4b: API-backed bookmarks + folders.
//
// list()/folders() hydrate the store; all the count/filter/derived helpers stay
// store-reading; mutations POST/PATCH/DELETE then patch the store.
// Signatures + _resetForTest unchanged.

import type { Bookmark, BookmarkFolder, BookmarkType } from '@/core/models/bookmark';
import { useBookmarkStore } from '@/core/stores/bookmarkStore';
import { seedBookmarks, seedBookmarkFolders } from '@/core/data/bookmarks';
import type { Post } from '@/core/models/post';
import { api } from '@/core/lib/apiClient';
import { useSessionStore } from '@/core/stores/sessionStore';

let generation = 0;
let reads = 0;
let folderReads = 0;
const account = (s: ReturnType<typeof useSessionStore.getState>) =>
  s.accounts.find((a) => a.useThisAsActive)?.userId;
useSessionStore.subscribe((next, previous) => {
  if (account(next) === account(previous)) return;
  generation++;
  useBookmarkStore.setState({ bookmarks: [], folders: [] });
});

function getBookmarks(): Bookmark[] {
  return useBookmarkStore.getState().bookmarks;
}

function getFolders(): BookmarkFolder[] {
  return useBookmarkStore.getState().folders;
}

export const bookmarksService = {
  invalidateReads(): void {
    reads++;
    folderReads++;
  },
  async list(): Promise<Bookmark[]> {
    const gen = generation;
    const read = ++reads;
    const list = await api<Bookmark[]>('/bookmarks/');
    if (gen !== generation || read !== reads) return [];
    useBookmarkStore.getState().setBookmarks(list);
    return [...list].sort((a, b) => b.savedAt - a.savedAt);
  },

  async byType(type: BookmarkType): Promise<Bookmark[]> {
    return getBookmarks().filter((b) => b.type === type);
  },

  async byFolder(folderId: string): Promise<Bookmark[]> {
    return getBookmarks().filter((b) => b.folderId === folderId);
  },

  async remove(id: string): Promise<void> {
    const gen = generation;
    reads++;
    await api(`/bookmarks/${id}/`, { method: 'DELETE' });
    if (gen !== generation) return;
    reads++;
    useBookmarkStore.getState().removeBookmark(id);
  },

  async folders(): Promise<BookmarkFolder[]> {
    const gen = generation;
    const read = ++folderReads;
    const folders = await api<BookmarkFolder[]>('/bookmark-folders/');
    if (gen !== generation || read !== folderReads) return [];
    useBookmarkStore.getState().setFolders(folders);
    return folders;
  },

  async folderById(folderId: string): Promise<BookmarkFolder | undefined> {
    return getFolders().find((f) => f.id === folderId);
  },

  async folderCount(folderId: string): Promise<number> {
    return getBookmarks().filter((b) => b.folderId === folderId).length;
  },

  async totalCount(): Promise<number> {
    return getBookmarks().length;
  },

  async thisWeekCount(): Promise<number> {
    return getBookmarks().filter((b) => b.recent).length;
  },

  async topFolder(): Promise<{ name: string; count: number }> {
    const folders = getFolders();
    const bookmarks = getBookmarks();
    let best = { name: 'None', count: 0 };
    for (const f of folders) {
      const c = bookmarks.filter((b) => b.folderId === f.id).length;
      if (c > best.count) best = { name: f.name, count: c };
    }
    return best;
  },

  async recentlySaved(n: number): Promise<Bookmark[]> {
    return [...getBookmarks()].sort((a, b) => b.savedAt - a.savedAt).slice(0, n);
  },

  async isBookmarked(postId: string): Promise<boolean> {
    return getBookmarks().some((b) => b.postId === postId && !b.commentId);
  },

  /** `folderId` (1.27.50, the watch page's Save): file it there instead of the default. */
  async add(post: Post, folderId?: string): Promise<Bookmark> {
    const gen = generation;
    reads++;
    const existing = getBookmarks().find((b) => b.postId === post.id && !b.commentId);
    if (existing) return existing;
    const bookmark = await api<Bookmark>('/bookmarks/', {
      method: 'POST',
      body: folderId ? { postId: post.id, folderId } : { postId: post.id },
    });
    if (gen === generation) {
      reads++;
      useBookmarkStore.getState().addBookmark(bookmark);
    }
    return bookmark;
  },

  async toggle(post: Post): Promise<boolean> {
    const existing = getBookmarks().find((b) => b.postId === post.id && !b.commentId);
    if (existing) {
      await this.remove(existing.id);
      return false;
    }
    await this.add(post);
    return true;
  },

  async createFolder(name: string): Promise<BookmarkFolder> {
    const gen = generation;
    const folder = await api<BookmarkFolder>('/bookmark-folders/', {
      method: 'POST',
      body: { name },
    });
    if (gen === generation) useBookmarkStore.getState().setFolders([...getFolders(), folder]);
    return folder;
  },

  async renameFolder(id: string, name: string): Promise<void> {
    const gen = generation;
    await api(`/bookmark-folders/${id}/`, { method: 'PATCH', body: { name } });
    if (gen !== generation) return;
    useBookmarkStore.getState().renameFolder(id, name);
  },

  async deleteFolder(id: string): Promise<void> {
    const gen = generation;
    await api(`/bookmark-folders/${id}/`, { method: 'DELETE' });
    if (gen !== generation) return;
    useBookmarkStore.getState().removeFolder(id);
  },

  async moveBookmark(bookmarkId: string, folderId: string): Promise<void> {
    const gen = generation;
    await api(`/bookmarks/${bookmarkId}/move/`, { method: 'POST', body: { folderId } });
    if (gen !== generation) return;
    useBookmarkStore.getState().setBookmarkFolder(bookmarkId, folderId);
  },

  _resetForTest(): void {
    useBookmarkStore.getState().setBookmarks(seedBookmarks.map((b) => ({ ...b })));
    useBookmarkStore.getState().setFolders(seedBookmarkFolders.map((f) => ({ ...f })));
  },
} as const;
